/** Display dates as DD-MM-YYYY across portals. Storage stays ISO YYYY-MM-DD. */

function pad(n) {
  return String(n).padStart(2, "0");
}

function calendarDate(year, month, day) {
  const dt = new Date(year, month - 1, day);
  if (dt.getFullYear() !== year || dt.getMonth() !== month - 1 || dt.getDate() !== day) return null;
  return dt;
}

export function isValidDmyDate(value) {
  const raw = String(value || "").trim();
  if (!/^\d{2}-\d{2}-\d{4}$/.test(raw)) return false;
  const [d, m, y] = raw.split("-").map(Number);
  return Boolean(calendarDate(y, m, d));
}

export function dmyToIso(value) {
  const raw = String(value || "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, d] = raw.split("-").map(Number);
    return calendarDate(y, m, d) ? raw : "";
  }
  if (!isValidDmyDate(raw)) return "";
  const [d, m, y] = raw.split("-");
  return `${y}-${m}-${d}`;
}

/** Comma-separated DD-MM-YYYY only. Returns valid ISO dates and any invalid tokens. */
export function parseBlockedDatesInput(text) {
  const tokens = String(text || "").split(",").map((s) => s.trim()).filter(Boolean);
  const validIso = [];
  const invalid = [];
  tokens.forEach((t) => {
    if (isValidDmyDate(t)) validIso.push(dmyToIso(t));
    else invalid.push(t);
  });
  return { tokens, validIso, invalid };
}

export function parseDateValue(value) {
  if (!value) return null;
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, d] = raw.split("-").map(Number);
    return calendarDate(y, m, d);
  }
  if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) {
    const [d, m, y] = raw.split("-").map(Number);
    return calendarDate(y, m, d);
  }
  const dt = new Date(raw);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

export function formatDateDMY(value) {
  const d = parseDateValue(value);
  if (!d) return value ? String(value) : "—";
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
}

export function formatDateTimeDMY(value) {
  const raw = String(value || "").trim();
  if (!raw) return "—";
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw) || /^\d{2}-\d{2}-\d{4}$/.test(raw)) {
    return formatDateDMY(raw);
  }
  const d = parseDateValue(value);
  if (!d) return raw;
  const ist = new Date(d.getTime() + (330 + d.getTimezoneOffset()) * 60000);
  return `${pad(ist.getUTCDate())}-${pad(ist.getUTCMonth() + 1)}-${ist.getUTCFullYear()} ${pad(ist.getUTCHours())}:${pad(ist.getUTCMinutes())}`;
}

export function greetingForNow(date = new Date()) {
  const h = date.getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

export function parseMapsPin(text) {
  const s = String(text || "").trim();
  if (!s) return null;
  const at = s.match(/@(-?\d+\.\d+)\s*,\s*(-?\d+\.\d+)/);
  if (at) return { lat: Number(at[1]), lng: Number(at[2]) };
  const q = s.match(/[?&]q=(-?\d+\.\d+)\s*,\s*(-?\d+\.\d+)/i);
  if (q) return { lat: Number(q[1]), lng: Number(q[2]) };
  const pair = s.match(/^(-?\d{1,2}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)$/);
  if (pair) return { lat: Number(pair[1]), lng: Number(pair[2]) };
  return null;
}

export function mapsUrlFor(lat, lng) {
  if (lat == null || lng == null || Number.isNaN(Number(lat)) || Number.isNaN(Number(lng))) return "";
  return `https://www.google.com/maps?q=${Number(lat)},${Number(lng)}`;
}

export function ageFromDob(dob) {
  const d = parseDateValue(dob);
  if (!d) return "";
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age -= 1;
  if (age < 0 || age > 120) return "";
  return age;
}

export function formatTime12(hhmm) {
  const raw = String(hhmm || "").trim();
  const m = raw.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return raw || "—";
  let h = Number(m[1]);
  const min = m[2];
  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${min} ${ap}`;
}

export function formatSlotRange(hour, endHour) {
  const start = String(hour || "").slice(0, 5);
  if (!/^\d{2}:\d{2}$/.test(start)) return hour || "—";
  let end = String(endHour || "").slice(0, 5);
  if (!/^\d{2}:\d{2}$/.test(end)) {
    const [h, m] = start.split(":").map(Number);
    const endM = h * 60 + m + 60;
    end = `${String(Math.floor((endM % (24 * 60)) / 60)).padStart(2, "0")}:${String((endM % (24 * 60)) % 60).padStart(2, "0")}`;
  }
  return `${formatTime12(start)} – ${formatTime12(end)}`;
}

export function roundInr(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return null;
  return Math.round(v);
}

export const FAMILY_RELATIONS = ["Self", "Child", "Parent", "Spouse", "Sibling", "Grandparent", "Other"];
